// Merkezi sipariş domain mantığı — client + server tek kaynak.
// Fiyat/kargo/kupon hesabı burada yapılır; checkout API ve CartContext aynı
// formülü kullanır (fiyat manipülasyon koruması + tutarlılık).

import { products } from "@/lib/products";
import { getCouponDiscount, foldCouponCode } from "@/lib/coupons";
import { calcBundle, type CatalogLike } from "@/lib/promotions";
import { EMAIL_RE as emailRegex, PHONE_RE as phoneRegex, POSTAL_RE as postalRegex, TURKISH_CITIES } from "@/lib/validate";

export type OrderStatus =
  | "pending"
  | "paid"
  | "hazirlaniyor"
  | "kargoda"
  | "teslim_edildi"
  | "iptal"
  | "iade";

export type OrderItemInput = { slug: string; quantity: number };

export type VerifiedOrderItem = {
  slug: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  variant?: string;
};

export type OrderCustomer = {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postal?: string;
  /** WhatsApp kargo bildirimi onayı — yoksa varsayılan true (eski kayıtlar). */
  whatsappOptIn?: boolean;
};

export type OrderTotals = {
  subtotal: number;
  bundleDiscount: number;
  secondDiscount: number;
  promoDiscount: number;
  coupon: string | null;
  couponDiscount: number;
  shipping: number;
  freeShip: boolean;
  total: number;
  itemCount: number;
};

/** Kargo politikası — hukuki sayfalarla (mesafeli satış/SSS) aynı değer. */
export const FREE_SHIPPING_THRESHOLD = 1500;
export const STANDARD_SHIPPING_FEE = 79.9;
/** Hızlı (ekspres) teslimat farkı — standart ücrete eklenir, bedava kargoda da geçerli değildir. */
export const EXPRESS_SHIPPING_EXTRA = 59.9;

export type ShippingMethod = "standart" | "hizli";
export const MAX_ORDER_AMOUNT = 500_000;
export const MAX_ITEMS_PER_ORDER = 50;
export const MAX_QTY_PER_LINE = 99;

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Sepet toplamını tek formülle hesapla (server + client ortak). */
export function calcOrderTotals(
  items: OrderItemInput[],
  coupon: string | null | undefined,
  opts?: { catalog?: CatalogLike[]; couponRate?: number; shippingFee?: number; freeShipThreshold?: number; expressExtra?: number; shippingMethod?: ShippingMethod }
): OrderTotals {
  const normalizedCoupon = foldCouponCode(coupon) || null;
  const catalog = opts?.catalog ?? products;
  const priceMap = new Map(catalog.map((p) => [p.slug, p]));

  let subtotal = 0;
  let itemCount = 0;
  for (const it of items) {
    const item = priceMap.get(it.slug);
    if (!item || !item.inStock) continue;
    const qty = Math.max(0, Math.min(MAX_QTY_PER_LINE, Math.floor(it.quantity) || 0));
    subtotal += item.price * qty;
    itemCount += qty;
  }
  subtotal = round2(subtotal);

  const bundle = calcBundle(items, catalog);
  const afterPromo = round2(subtotal - bundle.discount);
  const rate = opts?.couponRate ?? getCouponDiscount(normalizedCoupon);
  const couponDiscount = round2(afterPromo * rate);
  const afterCoupon = round2(afterPromo - couponDiscount);

  const freeShipThreshold = opts?.freeShipThreshold ?? FREE_SHIPPING_THRESHOLD;
  const shippingFee = opts?.shippingFee ?? STANDARD_SHIPPING_FEE;
  const expressExtra = opts?.expressExtra ?? EXPRESS_SHIPPING_EXTRA;
  const method: ShippingMethod = opts?.shippingMethod === "hizli" ? "hizli" : "standart";
  const freeShip = afterCoupon >= freeShipThreshold || afterCoupon <= 0;
  // Bedava kargo eşiği tutsa bile hızlı teslimat farkı alınır.
  const shipping = afterCoupon <= 0 ? 0 : round2((freeShip ? 0 : shippingFee) + (method === "hizli" ? expressExtra : 0));
  const total = round2(afterCoupon + shipping);

  return {
    subtotal,
    bundleDiscount: bundle.bundleDiscount,
    secondDiscount: bundle.secondDiscount,
    promoDiscount: bundle.discount,
    coupon: normalizedCoupon,
    couponDiscount,
    shipping,
    freeShip,
    total,
    itemCount,
  };
}

/** Sipariş no soneki — kriptografik rastgele (client+server uyumlu Web Crypto). */
function defaultRandSuffix(): string {
  try {
    const c = globalThis.crypto as
      | { randomUUID?: () => string; getRandomValues?: (a: Uint8Array) => Uint8Array }
      | undefined;
    if (c?.randomUUID) return c.randomUUID().replace(/-/g, "").slice(0, 6).toUpperCase();
    if (c?.getRandomValues) {
      const b = c.getRandomValues(new Uint8Array(6));
      const abc = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      return Array.from(b, (x) => abc[x % 36]).join("");
    }
  } catch {
    /* aşağıda Math.random fallback */
  }
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

/** ONO-20260914-XXXXXX formatında benzersiz sipariş no. */
export function generateOrderId(now: number = Date.now(), rand: string = defaultRandSuffix()): string {
  const d = new Date(now);
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  const suffix = (rand + "XXXXXX").slice(0, 6).replace(/[^A-Z0-9]/g, "X");
  return `ONO-${ymd}-${suffix}`;
}

export function isValidOrderId(id: string): boolean {
  return /^ONO-\d{8}-[A-Z0-9]{6}$/.test(id) || /^ONO-\d+-[A-Z0-9]{4,6}$/.test(id) || /^\d{4,}$/.test(id);
}

/** Deterministik kargo takip no — sipariş id'den türetilir, stabil. */
export function generateTrackingNumber(orderId: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < orderId.length; i++) {
    h ^= orderId.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  const num = String(h % 100_000_000).padStart(8, "0");
  const check = String((h % 9) + 1);
  return `TRK${num}${check}26`;
}

export function getCarrier(): { name: string; etaDays: string } {
  return { name: "Yurtiçi Kargo", etaDays: "1-3 iş günü" };
}

/** Locale-bağımsız küçük harf + Türkçe karakter katlama.
 *  `toLocaleLowerCase("tr-TR")` "SHIPPED"→"shıpped", "YURTICI"→"yurtıcı" üretip
 *  eşleşmeleri öldürür (K6/Y8). Bu yardımcı her girdiyi ASCII'ye katlar:
 *  "YURTICI"/"Yurtiçi"→"yurtici", "SHIPPED"→"shipped", "Dağıtımda"→"dagitimda". */
export function foldLower(s: string): string {
  return (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i");
}
/** Taşıyıcı takip sayfası (harici link) — firma sorgu ekranına yönlendirir.
 *  Takip no varsa sorgu parametresine işlenir; yoksa taşıyıcının genel
 *  takip sayfasına düşülür. Site içi takip no dahili üretildiği için
 *  manuel modda taşıyıcı sitesinde bulunamayabilir (o zaman site içi takip kullanılır). */
export function getCarrierTrackingUrl(carrier: string, trackingNumber: string): string | null {
  const c = foldLower(carrier);
  const no = (trackingNumber ?? "").trim();
  if (!no) return null;
  const q = encodeURIComponent(no);
  if (c.includes("yurtici"))
    return `https://www.yurticikargo.com/tr/online-servisler/gonderi-sorgula?code=${q}`;
  if (c.includes("aras")) return `https://www.araskargo.com.tr/tr/gonderi-takibi?kargoTakipNo=${q}`;
  if (c.includes("mng")) return `https://www.mngkargo.com.tr/gonderi-takibi?bcd=${q}`;
  if (c.includes("surat")) return `https://www.suratkargo.com.tr/kargo-takibi?kargoTakipNo=${q}`;
  if (c.includes("ptt")) return `https://gonderitakip.ptt.gov.tr/?barkod=${q}`;
  if (c.includes("ups")) return `https://www.ups.com/track?tracknum=${q}`;
  if (c.includes("hepsijet") || c.includes("hepsi jet") || c.includes("hepsi")) return `https://www.hepsijet.com/gonderi-takibi?trackingNumber=${q}`;
  if (c.includes("kolay")) return `https://www.kolaygelsin.com/takip?code=${q}`;
  if (c.includes("sendeo")) return `https://www.sendeo.com.tr/gonderi-takibi?no=${q}`;
  return null;
}

/** Taşıyıcı adını normalize et (provider eşleşmesi + ENV prefix için tek kaynak). */
export function normalizeCarrier(carrier: string): string {
  const c = foldLower(carrier);
  if (c.includes("yurtici")) return "yurtici";
  if (c.includes("aras")) return "aras";
  if (c.includes("mng")) return "mng";
  if (c.includes("surat")) return "surat";
  if (c.includes("ptt")) return "ptt";
  if (c.includes("ups")) return "ups";
  if (c.includes("hepsijet") || c.includes("hepsi")) return "hepsijet";
  if (c.includes("kolay")) return "kolaygelsin";
  if (c.includes("sendeo")) return "sendeo";
  return "manual";
}

/** Desteklenen taşıyıcı listesi (admin seçimi + takip URL'leri tek kaynak). */
export const SUPPORTED_CARRIERS = [
  "Yurtiçi Kargo",
  "Aras Kargo",
  "MNG Kargo",
  "Sürat Kargo",
  "PTT Kargo",
  "UPS",
  "HepsiJet",
  "Kolay Gelsin",
  "Sendeo",
] as const;

/** Tahmini teslimat: Pazar hariç +3 iş günü. */
export function estimatedDelivery(from: Date = new Date()): Date {
  const d = new Date(from);
  let added = 0;
  while (added < 3) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) added += 1;
  }
  return d;
}

export function formatDeliveryDate(d: Date): string {
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "long" });
}

export function formatOrderDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

export const ORDER_STATUS_META: Record<OrderStatus, { label: string; cls: string }> = {
  pending: { label: "Ödeme Bekliyor", cls: "bg-amber-100 text-amber-800" },
  paid: { label: "Ödendi", cls: "bg-emerald-100 text-emerald-800" },
  hazirlaniyor: { label: "Hazırlanıyor", cls: "bg-amber-100 text-amber-800" },
  kargoda: { label: "Kargoda", cls: "bg-sky-100 text-sky-800" },
  teslim_edildi: { label: "Teslim Edildi", cls: "bg-emerald-100 text-emerald-800" },
  iptal: { label: "İptal", cls: "bg-zinc-200 text-zinc-600" },
  iade: { label: "İade Edildi", cls: "bg-purple-100 text-purple-800" },
};

export function canCancelOrder(status: OrderStatus): boolean {
  return status === "pending" || status === "paid" || status === "hazirlaniyor";
}

export const ORDER_TIMELINE = [
  { key: "hazirlaniyor", label: "Hazırlanıyor" },
  { key: "kargoda", label: "Kargoda" },
  { key: "teslim_edildi", label: "Teslim Edildi" },
] as const;

// --- Validasyon ---
// Regex + şehir listesinin tek kaynağı: src/lib/validate.ts (zod şemalarıyla paylaşılır).

export { TURKISH_CITIES };

export function validateCustomer(customer: Partial<OrderCustomer> & { kvkk?: boolean; contract?: boolean; preinfo?: boolean }): string | null {
  const name = customer.name?.trim() ?? "";
  if (name.length < 3 || name.length > 100) return "Ad Soyad 3-100 karakter olmalı";
  const email = customer.email?.trim() ?? "";
  if (!emailRegex.test(email) || email.length > 254) return "Geçersiz e-posta";
  if (!customer.phone || !phoneRegex.test(customer.phone.trim())) return "Geçersiz telefon";
  const address = customer.address?.trim() ?? "";
  if (address.length < 10 || address.length > 500) return "Adres 10-500 karakter olmalı";
  const city = customer.city?.trim() ?? "";
  if (!TURKISH_CITIES.includes(city)) return "Geçerli bir şehir seçin";
  if (customer.postal?.trim() && !postalRegex.test(customer.postal.trim())) return "Posta kodu 5 haneli olmalı";
  if (customer.kvkk !== true) return "KVKK onayı gerekli";
  if (customer.contract !== true) return "Mesafeli satış sözleşmesi onayı gerekli";
  if (customer.preinfo !== true) return "Ön bilgilendirme onayı gerekli";
  return null;
}

export function validateItems(items: unknown): string | null {
  if (!Array.isArray(items) || items.length === 0) return "Sepet boş";
  if (items.length > MAX_ITEMS_PER_ORDER) return "Sepet çok büyük (en fazla 50 satır)";
  for (const it of items as { quantity?: unknown; slug?: unknown; name?: unknown }[]) {
    const qty = Math.floor(Number(it.quantity));
    if (!qty || qty < 1 || qty > MAX_QTY_PER_LINE) return "Geçersiz adet (1-99)";
    if (typeof it.slug !== "string" && typeof it.name !== "string") return "Geçersiz ürün";
  }
  return null;
}
